import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Popover, PopoverContent } from './popover';
import { SelectSearchInput, SelectTrigger, type SelectTriggerVariant } from './select-primitives';
import { cn } from '@/lib/utils';

/* ── Single select ── */

export function SelectPopover({
	value,
	onChange,
	options,
	placeholder = 'Select…',
	variant = 'form',
	className,
}: {
	value: string;
	onChange: (v: string) => void;
	options: Array<{ value: string; label: string }>;
	placeholder?: string;
	variant?: SelectTriggerVariant;
	className?: string;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	const filtered = useMemo(() => {
		if (!query) return options;
		const q = query.toLowerCase();
		return options.filter((o) => o.label.toLowerCase().includes(q));
	}, [options, query]);

	const selectedLabel = options.find((o) => o.value === value)?.label;

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery('');
			}}
		>
			<SelectTrigger
				label={selectedLabel}
				placeholder={placeholder}
				hasValue={!!value}
				variant={variant}
				className={className}
			/>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<SelectSearchInput value={query} onChange={setQuery} />
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{filtered.length === 0 ? (
						<div className="px-2 py-3 text-center text-sm text-text-4">No results</div>
					) : (
						filtered.map((opt) => (
							<button
								key={opt.value}
								onClick={() => {
									onChange(opt.value);
									setOpen(false);
									setQuery('');
								}}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
									value === opt.value
										? 'bg-surface-fill text-text-1'
										: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
								)}
							>
								<Check
									size={12}
									className={cn(
										'shrink-0',
										value === opt.value ? 'text-accent' : 'invisible'
									)}
								/>
								{opt.label}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
